import React, { useEffect, useState } from 'react';
import { useAuth, apiGet } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Calendar, Users, Music, Bed, Camera, Headphones, Gift, Table2, CheckCircle, X } from 'lucide-react';
import { API_BASE_URL } from '@/lib/api';
import { usePublicContent } from '@/lib/content';

const API = API_BASE_URL;

export default function PortalDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rsvp, setRsvp] = useState(null);
  const [table, setTable] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const { event_information: eventInfo } = usePublicContent();

  useEffect(() => {
    apiGet('/api/rsvp/me').then(r => setRsvp(r.data.rsvp)).catch(() => {});
    apiGet('/api/tables/mine').then(r => setTable(r.data.table)).catch(() => {});
  }, []);

  const tiles = [
    { icon: Calendar, title: 'Nedtælling', desc: 'Status for eventdagen', color: 'blue', path: '/portal/briefing', span: 'md:col-span-2' },
    { icon: Calendar, title: 'RSVP', desc: rsvp ? (rsvp.attending ? 'Bekræftet' : 'Afvist') : 'Send dit RSVP', color: 'blue', path: '/portal/briefing', span: 'md:col-span-2' },
    { icon: Users, title: 'Min profil', desc: 'Se din gæsteprofil', color: 'purple', path: '/portal/profile' },
    { icon: Music, title: 'Musikønsker', desc: 'Ønsk dine sange', color: 'indigo', path: '/portal/music' },
    { icon: Bed, title: 'Overnatning', desc: user?.overnight_allowed ? 'Overnatning mulig' : 'Info', color: 'teal', path: '/portal/accommodation' },
    { icon: Headphones, title: 'Spørgsmål og svar', desc: 'Praktisk hjælp', color: 'amber', path: '/portal/support' },
    { icon: Gift, title: 'Ønskeliste', desc: 'Gaveidéer', color: 'pink', path: '/portal/wishes' },
    { icon: Camera, title: 'Fotoprogram', desc: 'Del billeder', color: 'emerald', path: '/portal/photos' },
  ];
  const qrUrl = (user?._id || user?.id) ? `${API}/api/guests/${user._id || user.id}/qr` : null;
  const guestName = user?.full_name || 'Gæst';

  const colorMap = {
    blue: 'from-blue-500/20 to-blue-600/5 border-blue-500/20 hover:border-blue-500/40',
    purple: 'from-purple-500/20 to-purple-600/5 border-purple-500/20 hover:border-purple-500/40',
    indigo: 'from-indigo-500/20 to-indigo-600/5 border-indigo-500/20 hover:border-indigo-500/40',
    teal: 'from-teal-500/20 to-teal-600/5 border-teal-500/20 hover:border-teal-500/40',
    amber: 'from-amber-500/20 to-amber-600/5 border-amber-500/20 hover:border-amber-500/40',
    pink: 'from-pink-500/20 to-pink-600/5 border-pink-500/20 hover:border-pink-500/40',
    emerald: 'from-emerald-500/20 to-emerald-600/5 border-emerald-500/20 hover:border-emerald-500/40',
  };
  const iconColor = { blue: 'text-blue-400', purple: 'text-purple-400', indigo: 'text-indigo-400', teal: 'text-teal-400', amber: 'text-amber-400', pink: 'text-pink-400', emerald: 'text-emerald-400' };

  return (
    <div className="max-w-6xl mx-auto animate-fade-in" data-testid="portal-dashboard">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-50" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Velkommen tilbage, {user?.full_name?.split(' ')[0] || 'gæst'}
        </h1>
        <p className="text-sm text-slate-400 mt-1 font-mono break-all">{user?.display_email || user?.private_email || user?.corporate_email}</p>
      </div>

      {eventInfo.practical_information && (
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 mb-6" data-testid="dashboard-practical-info">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-2">Praktisk information</p>
          <p className="text-sm text-slate-300 leading-relaxed">{eventInfo.practical_information}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {rsvp && (
          <div className={`rounded-xl border p-4 flex items-center gap-3 ${rsvp.attending ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'}`} data-testid="rsvp-status-bar">
            <Calendar className={`w-5 h-5 ${rsvp.attending ? 'text-emerald-400' : 'text-red-400'}`} />
            <div>
              <span className={`text-sm font-medium ${rsvp.attending ? 'text-emerald-400' : 'text-red-400'}`}>
                {rsvp.attending ? 'RSVP bekræftet' : 'RSVP afvist'}
              </span>
              {rsvp.guest_count > 1 && <span className="text-xs text-slate-500 ml-2">+{rsvp.guest_count - 1} gæst(er)</span>}
            </div>
          </div>
        )}

        {table && (
          <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-4 flex items-center gap-3" data-testid="table-assignment-bar">
            <Table2 className="w-5 h-5 text-cyan-400" />
            <div>
              <span className="text-sm font-medium text-cyan-400">{table.name}</span>
              <p className="text-xs text-slate-500">Din bordplacering</p>
            </div>
          </div>
        )}

        {user?.checkin_status === 'checked_in' && (
          <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-4 flex items-center gap-3" data-testid="checkin-status-bar">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <div>
              <span className="text-sm font-medium text-green-400">Tjekket ind</span>
              <p className="text-xs text-slate-500">{user.checkin_at ? new Date(user.checkin_at).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' }) : ''}</p>
            </div>
          </div>
        )}
      </div>

      {qrUrl && (
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-4 text-center sm:text-left" data-testid="qr-badge">
          <button type="button" onClick={() => setQrOpen(true)} className="flex-shrink-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="open-dashboard-qr">
            <img src={qrUrl} alt="Event-QR" className="w-16 h-16 rounded-lg border border-slate-700 bg-white p-1" />
          </button>
          <div className="min-w-0">
            <p className="text-sm text-slate-200 font-medium">Dit event-QR</p>
            <p className="text-xs text-slate-500">Tryk for stor visning ved indgangen.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {tiles.map((tile, i) => (
          <button key={tile.title} onClick={() => navigate(tile.path)}
            className={`${tile.span || ''} text-left bg-gradient-to-br ${colorMap[tile.color]} border rounded-xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg animate-fade-in`}
            style={{ animationDelay: `${i * 0.07}s`, opacity: 0 }}
            data-testid={`tile-${tile.title.toLowerCase().replace(/\s/g, '-')}`}>
            <tile.icon className={`w-8 h-8 ${iconColor[tile.color]} mb-4`} />
            <h3 className="text-base font-medium text-slate-200 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{tile.title}</h3>
            <p className="text-xs text-slate-400">{tile.title === 'Nedtælling' ? getCountdownLabel() : tile.desc}</p>
          </button>
        ))}
      </div>

      {qrOpen && qrUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4" role="dialog" aria-modal="true" data-testid="dashboard-qr-modal">
          <div className="w-full max-w-[min(92vw,520px)] max-h-[calc(100vh-2rem)] overflow-y-auto rounded-xl border border-slate-700 bg-slate-950 p-4 sm:p-5 text-center shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <div className="text-left">
                <h2 className="text-base font-semibold text-slate-100" style={{ fontFamily: "'Outfit', sans-serif" }}>Dit event-QR</h2>
                <p className="text-sm text-slate-400">{guestName}</p>
              </div>
              <button type="button" onClick={() => setQrOpen(false)} className="min-h-10 min-w-10 rounded-lg text-slate-400 hover:text-slate-100 hover:bg-slate-800" aria-label="Luk QR">
                <X className="w-5 h-5 mx-auto" />
              </button>
            </div>
            <div className="rounded-2xl bg-white p-4 sm:p-5 shadow-[0_0_50px_rgba(255,255,255,0.28)]">
              <img src={qrUrl} alt="Stor event-QR" className="w-full max-w-[min(78vw,420px)] mx-auto" />
            </div>
            <p className="mt-4 text-base font-medium text-slate-100">Vis denne QR-kode ved indgangen</p>
          </div>
        </div>
      )}
    </div>
  );
}

function getCountdownLabel() {
  const eventDate = new Date('2027-02-27T15:00:00+01:00');
  const diff = eventDate.getTime() - Date.now();
  if (diff <= 0) return 'Molin30 er live';
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  return `${days} dag${days === 1 ? '' : 'e'} til Molin30`;
}
